import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MapInfoWindow } from '@angular/google-maps';
import { mergeDeep } from '@cargos/sprintpay-tools';
import { Observable, Subject, from, map, of, switchMap, take, takeUntil } from 'rxjs';
import { ThemeType } from 'src/app/shared/models';
import { ThemeService } from 'src/app/shared/services';
import { GoogleMapService } from 'src/app/shared/services/google-map.service';
import { environment } from 'src/environments/environment';
import { AdvancedMarkerElementOptionsCustom } from './models/advanced-marker-element-options';

@Component({
    selector: 'app-google-map',
    templateUrl: './google-map.component.html',
    styleUrls: ['./google-map.component.scss'],
    standalone: false,
})
export class GoogleMapComponent implements OnInit, OnDestroy {
    private unsubscribe$: Subject<void>;
    public isGoogleMapsLoaded = false;
    public infoLabel = '';
    public mapOptionsConfig: google.maps.MapOptions;
    public markersList: google.maps.marker.AdvancedMarkerElement[] = [];
    public isLoadingGoogleMaps = false;
    public googleMapId = environment.googleMapIdLight;

    @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow;

    @Input() markers: AdvancedMarkerElementOptionsCustom[] = [];
    @Input() set mapOptions(mapOptions: google.maps.MapOptions) {
        this.mapOptionsConfig = mapOptions;
    }
    @Output() eventOnChangeMarker = new EventEmitter<number>();

    constructor(
        private googleMapService: GoogleMapService,
        private themeService: ThemeService
    ) {
        this.unsubscribe$ = new Subject<void>();
    }

    ngOnInit(): void {
        this.loadGoogleMapsAPI();
        if (!!this.markers?.length) {
            this.initGoogleMaps();
        }
        this.getGoogleMapId();
        this.subscribeLoadingMaps();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    subscribeLoadingMaps(): void {
        this.googleMapService
            .isLoadingGoogleMaps()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((isLoadingGoogleMaps) => {
                this.isLoadingGoogleMaps = isLoadingGoogleMaps;
            });
    }

    loadGoogleMapsAPI(): void {
        this.googleMapService.loadAPI().pipe(take(1)).subscribe();
    }

    initGoogleMaps(): void {
        this.googleMapService
            .isGoogleMapsLoaded()
            .pipe(
                switchMap((isGoogleMapsLoaded) => {
                    if (isGoogleMapsLoaded) {
                        return from(this.createMarkers(this.markers)).pipe(
                            map((markers) => ({
                                isGoogleMapsLoaded,
                                markers,
                            }))
                        );
                    }

                    return of({ isGoogleMapsLoaded, markers: [] });
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: ({
                    isGoogleMapsLoaded,
                    markers,
                }: {
                    isGoogleMapsLoaded: boolean;
                    markers: google.maps.marker.AdvancedMarkerElement[];
                }) => {
                    const isLoadedMapInBrowser = this.checkGoogleMapLoaded();
                    this.isGoogleMapsLoaded = isGoogleMapsLoaded && isLoadedMapInBrowser;

                    if (isGoogleMapsLoaded) {
                        this.markersList = markers;
                        const centerMap = this.googleMapService.getCenterMap(markers);

                        this.setMapOptions({
                            center: centerMap,
                        });
                    }
                },
            });
    }

    createMarkers(
        markers: AdvancedMarkerElementOptionsCustom[]
    ): Observable<google.maps.marker.AdvancedMarkerElement[]> {
        return from(this.googleMapService.createMarkers(markers));
    }

    openInfoWindow(marker: google.maps.marker.AdvancedMarkerElement, indexMarker: number): void {
        this.infoWindow.openAdvancedMarkerElement(marker, marker.title);

        this.eventOnChangeMarker.emit(indexMarker);
    }

    getGoogleMapId(): void {
        this.themeService
            .getTheme()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (themeType) => {
                    if (themeType === ThemeType.light) {
                        this.googleMapId = environment.googleMapIdLight;
                    } else {
                        this.googleMapId = environment.googleMapIdDark;
                    }
                },
            });
    }

    setMapOptions(options: google.maps.MapOptions = {}): void {
        const defaultOptions = {
            mapTypeControl: false,
            zoom: 6,
            mapId: this.googleMapId,
            ...this.mapOptionsConfig,
        };
        const optionsMap = mergeDeep(defaultOptions, options);

        this.mapOptionsConfig = optionsMap as google.maps.MapOptions;
    }

    checkGoogleMapLoaded(): boolean {
        return typeof window.google === 'object' && typeof window.google.maps === 'object';
    }
}
